<template>
    <div class="league-standings" :class="{ 'league-standings--compact': compact }">
        <v-data-table
            :headers="compact ? compactHeaders : headers"
            :items="ranking"
            class="cl-table"
            hide-default-footer
            disable-sort
            :mobile-breakpoint="0"
            :items-per-page="-1"
            dark
            no-data-text="Δεν υπάρχει ακόμη βαθμολογία"
        >
            <template v-slot:item.position="{ item }">
                <span class="cl-pos" :class="{ 'cl-pos--top': ranking.indexOf(item) === 0 }">{{ ranking.indexOf(item) + 1 }}</span>
            </template>
            <template v-slot:item.club="{ item }">
                <div class="standings-club" @click="$router.push({ name: 'Team', params: { id: item.id } })">
                    <span class="cl-logo standings-club__logo">
                        <img v-if="item.imageUrl" :src="`${assetsUrl}/${item.imageUrl}`" :alt="item.name">
                        <v-icon v-else color="teal"> mdi-basketball-hoop </v-icon>
                    </span>
                    <span class="standings-club__name">{{ item.name }}</span>
                </div>
            </template>
            <template v-slot:item.standing="{ item }">
                <span class="standings-points">{{ item.standing }}</span>
            </template>
            <template v-slot:item.gp="{ item }">
                {{ (item.won + item.lost) }}
            </template>
            <template v-slot:item.win_percentage="{ item }">
                {{ `${(item.won + item.lost) > 0 ? (item.won / (item.won + item.lost) * 100).toFixed(1) : 0}%` }}
            </template>
            <template v-slot:item.pointsDiff="{ item }">
                <span :class="item.pointsDiff > 0 ? 'standings-diff--plus' : item.pointsDiff < 0 ? 'standings-diff--minus' : ''">
                    {{ item.pointsDiff > 0 ? `+${item.pointsDiff}` : item.pointsDiff }}
                </span>
            </template>
        </v-data-table>
    </div>
</template>

<script>

    export default {
        props: {
            ranking: {
                type: Array,
                required: true
            },
            // the home page: half a row each, so only the key columns (the full table is on the league page)
            compact: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                headers: [
                    { text: '#', value: 'position', width: '48px', align: 'center' },
                    { text: 'Club', value: 'club', align: 'start' },
                    { text: 'Points', value: 'standing', width: '70px', align: 'center' },
                    { text: 'GP', value: 'gp', width: '50px', align: 'center' },
                    { text: 'Won', value: 'won', width: '50px', align: 'center' },
                    { text: 'Lost', value: 'lost', width: '50px', align: 'center' },
                    { text: 'Win(%)', value: 'win_percentage', width: '70px', align: 'center' },
                    { text: 'Pts+', value: 'pointsPlus', width: '60px', align: 'center' },
                    { text: 'Pts-', value: 'pointsMinus', width: '60px', align: 'center' },
                    { text: '+/-', value: 'pointsDiff', width: '60px', align: 'center' }
                ],
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            compactHeaders() {
                const headers = [
                    { text: '#', value: 'position', width: '44px', align: 'center' },
                    { text: 'Ομάδα', value: 'club', align: 'start' },
                    { text: 'GP', value: 'gp', width: '40px', align: 'center' },
                    { text: 'W', value: 'won', width: '40px', align: 'center' },
                    { text: 'L', value: 'lost', width: '40px', align: 'center' },
                    { text: '+/-', value: 'pointsDiff', width: '52px', align: 'center' },
                    { text: 'PTS', value: 'standing', width: '52px', align: 'center' }
                ]
                // phones: GP and +/- make room for the names
                return this.$vuetify.breakpoint.xs ? headers.filter(header => header.value !== 'gp' && header.value !== 'pointsDiff') : headers
            }
        }
    }
</script>

<style scoped>
.standings-club {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 200px;
    cursor: pointer;
}

.standings-club__logo {
    width: 36px;
    height: 36px;
}

.standings-club__name {
    font-weight: 700;
    white-space: nowrap;
}

.standings-club:hover .standings-club__name {
    color: #03e5b7;
}

.standings-points {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.35rem;
    color: #03e5b7;
}

.standings-diff--plus {
    color: #2ee6a6;
    font-weight: 700;
}

.standings-diff--minus {
    color: #ff6b6b;
    font-weight: 700;
}

/* ---------- compact (home page) ---------- */
.league-standings--compact ::v-deep table {
    table-layout: fixed;
    width: 100%;
}

.league-standings--compact ::v-deep th,
.league-standings--compact ::v-deep td {
    padding: 0 6px !important;
}

.league-standings--compact ::v-deep th:first-child,
.league-standings--compact ::v-deep td:first-child {
    padding-left: 12px !important;
}

.league-standings--compact ::v-deep th:last-child,
.league-standings--compact ::v-deep td:last-child {
    padding-right: 14px !important;
    background: rgba(3, 229, 183, 0.06);
}

.league-standings--compact ::v-deep tbody td {
    height: 50px !important;
}

.league-standings--compact .standings-club {
    gap: 10px;
    min-width: 0;
}

.league-standings--compact .standings-club__logo {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
}

/* long names: two lines, then an ellipsis, never cut by the edge of the table */
.league-standings--compact .standings-club__name {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 2;
    overflow: hidden;
    white-space: normal;
    line-height: 1.2;
    font-size: 0.85rem;
}
</style>
